import { useEffect, useState, type ReactNode } from "react";

type Frame = { key: string; content: ReactNode };
type SlideState = { current: Frame | null; outgoing: Frame | null; direction: "forward" | "back" | "refresh" };
type Props = { scopeKey: string; direction: SlideState["direction"]; phase: "loading" | "ready" | "error"; children: ReactNode; loading?: ReactNode };

export function nextAnalysisSlide(state: SlideState, frame: Frame | null, direction: SlideState["direction"], phase: Props["phase"]): SlideState {
  if (phase === "error") {
    return state.current ? { current: null, outgoing: null, direction: "refresh" } : state;
  }
  if (!frame || phase !== "ready" || frame.content === state.current?.content) return state;
  const changed = state.current && state.current.key !== frame.key;
  return { current: frame, outgoing: changed && direction !== "refresh" ? state.current : state.outgoing, direction: changed ? direction : state.direction };
}

export function ProductionAnalysisSlide({ scopeKey, direction, phase, children, loading }: Props) {
  const [state, setState] = useState<SlideState>({ current: phase === "ready" ? { key: scopeKey, content: children } : null, outgoing: null, direction });
  const next = nextAnalysisSlide(state, children ? { key: scopeKey, content: children } : null, direction, phase);
  if (next !== state) setState(next);
  useEffect(() => {
    if (!state.outgoing) return;
    const timer = window.setTimeout(() => setState(value => ({ ...value, outgoing: null })), 400);
    return () => window.clearTimeout(timer);
  }, [state.outgoing]);
  return <div className="pa-slide-viewport" data-direction={state.direction} data-sliding={Boolean(state.outgoing)}>
    {!state.current && phase === "loading" ? loading : null}
    {[state.outgoing, state.current].filter((frame): frame is Frame => frame !== null).map(frame => {
      const outgoing = frame === state.outgoing;
      return <div key={frame.key} className={`pa-analysis-step ${outgoing ? "pa-slide-outgoing" : "pa-slide-current"}`} inert={outgoing || phase !== "ready"} aria-hidden={outgoing || phase !== "ready" ? true : undefined}>{frame.content}</div>;
    })}
  </div>;
}
